import React, { useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import SkeletonLoader from "UIComponents/SkeletonLoader";

import { ProfileOutlined } from "@toddle-edu/ds-icons";
import { IconButton, Popover, Tooltip } from "@toddle-edu/ds-web";

import ProfileCascade from "UIComponents/ProfileCascade";
import Loading from "UIComponents/Loading";
import { useHandleScroll, useTextOverflow } from "Utils/CustomHooks";

import classes from "./BasicAccessPoint.scss";
import _ from "lodash";

const defaultAvatarStyles = {
  size: "medium",
  randomizeColor: true,
  shade: "light",
  shape: "square",
};

const BasicAccessPopoverContent = props => {
  const {
    id,
    name,
    subtitle1,
    footerComponent = null,
    actionComponent = null,
    onProfileClick,
    isProfileClickActionLoading = false,
    profileImage,
    isArchived = false,
    avatarStyles = defaultAvatarStyles,
  } = props;
  const handleClick = event => event.stopPropagation();
  const [isNameOverflowing, nameContainerRef] = useTextOverflow();

  return (
    <div className={classes.accessPopoverRoot} onClick={handleClick}>
      <div className={classes.accessPopoverProfileAndAction}>
        <div className={classes.accessPopoverHeader}>
          <ProfileCascade
            items={[{ id, value: name, imageUrl: profileImage }]}
            isArchived={isArchived}
            showAvatar={true}
            avatarStyles={avatarStyles}
          />
          <div className={classes.accessPopoverContent}>
            <Tooltip
              dsVersion="2.0"
              tooltip={name}
              placement="top"
              visible={isNameOverflowing}
            >
              <div
                ref={nameContainerRef}
                className={classNames("text-label", classes.name)}
              >
                {name}
              </div>
            </Tooltip>
            {subtitle1 ? (
              <div className={classNames("text-body-s", classes.textSubtle)}>
                {subtitle1}
              </div>
            ) : null}
          </div>
        </div>
        <div className={classes.accessPopoverActions}>
          {!isProfileClickActionLoading ? (
            <IconButton
              size="small"
              dsVersion="2.0"
              icon={<ProfileOutlined />}
              onClick={onProfileClick}
              variant="neutral"
              type="outlined"
            />
          ) : (
            <SkeletonLoader width={28} height={28} />
          )}
          {actionComponent}
        </div>
      </div>
      {footerComponent ? (
        <div className={classes.accessPopoverFooter}>{footerComponent}</div>
      ) : null}
    </div>
  );
};

const MultipleBasicAccessPointContent = props => {
  const {
    accessPointsProps = [],
    isLoading,
    isData,
    networkStatus,
    loadMore,
    hasNextPage,
  } = props;

  const scrollContainerRef = useRef();

  const getScrollContainerRef = () => {
    return scrollContainerRef.current;
  };

  useHandleScroll({
    networkStatus,
    loadMoreFeed: loadMore,
    getScrollRef: getScrollContainerRef,
    hasNextPage,
  });

  const isFetchingMoreData = networkStatus === 3;

  if (isLoading && !isData) {
    return (
      <div
        className={classNames(classes.multipleAccessPointsRoot, classes.center)}
      >
        <Loading position="center" />
      </div>
    );
  }

  return (
    <div className={classes.multipleAccessPointsRoot} ref={scrollContainerRef}>
      {_.map(accessPointsProps, (accessPointProps, index) => {
        const showDivider = _.size(accessPointsProps) !== index + 1;

        return (
          <>
            <BasicAccessPopoverContent
              key={_.get(accessPointProps, "id")}
              {...props}
              {...accessPointProps}
            />
            {showDivider ? <div className={classes.divider} /> : null}
          </>
        );
      })}
      {isFetchingMoreData ? <Loading /> : null}
    </div>
  );
};

const BasicAccessPoint = props => {
  const {
    children,
    openPopover,
    closePopover,
    isPopoverOpen,
    popupWrapperCustomClasses,
    name,
  } = props;

  const popupWrapperClasses = classNames(
    classes.popupWrapper,
    popupWrapperCustomClasses
  );

  const handlePopoverBlur = e => {
    const nextFocus = e.relatedTarget;

    if (nextFocus && e.currentTarget.contains(nextFocus)) return;
    if (nextFocus?.closest(`.${classes.popupContainer}`)) return;

    closePopover();
  };

  return (
    <div
      onMouseOver={openPopover}
      onFocus={openPopover}
      onMouseLeave={closePopover}
      onBlur={handlePopoverBlur}
      tabIndex={0}
      role="button"
      aria-label={
        name ? `Show profile details for ${name}` : "Show profile details"
      }
      aria-haspopup="true"
      aria-expanded={isPopoverOpen}
      className={popupWrapperClasses}
    >
      <Popover
        isOpen={isPopoverOpen}
        onClickOutside={closePopover}
        isClosable={false}
        align="start"
        placement="rightTop"
        containerClassName={classes.popupContainer}
        body={<BasicAccessPopoverContent {...props} />}
      >
        <div className={classes.popupChildren}>{children}</div>
      </Popover>
    </div>
  );
};

const MultiBasicAccessPoint = props => {
  const { children, openPopover, closePopover, isPopoverOpen } = props;

  return (
    <div
      onMouseOver={openPopover}
      onFocus={openPopover}
      onMouseLeave={closePopover}
      onBlur={closePopover}
      className={classes.popupWrapper}
    >
      <Popover
        isOpen={isPopoverOpen}
        onClickOutside={closePopover}
        isClosable={false}
        align="start"
        placement="rightTop"
        containerClassName={classes.popupContainer}
        body={<MultipleBasicAccessPointContent {...props} />}
      >
        <div className={classes.popupChildren}>{children}</div>
      </Popover>
    </div>
  );
};

BasicAccessPoint.propTypes = {
  children: PropTypes.node,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  subtitle1: PropTypes.node,
  footerComponent: PropTypes.node,
  actionComponent: PropTypes.node,
  onProfileClick: PropTypes.func.isRequired,
  isProfileClickActionLoading: PropTypes.bool,
  profileImage: PropTypes.string,
  isArchived: PropTypes.bool,
  avatarStyles: PropTypes.object,
};

MultiBasicAccessPoint.propTypes = {
  accessPointsProps: PropTypes.array,
  isLoading: PropTypes.bool,
  isData: PropTypes.bool,
};

export { MultiBasicAccessPoint, BasicAccessPoint };
